// Utilitários puros do painel de Meios de pagamento — agrupamento em duas
// dimensões, filtros client-side e regras de certificado.
// Mantidos sem React p/ teste unitário direto (vitest).
import type {
  ProcessorCertificateStatus,
  ProcessorConfigItem,
  ProcessorGateway,
  ProcessorPaymentMethod,
} from "@/types/admin-processor-config"

// Filtros locais do painel (client-side sobre a página carregada).
export interface PanelFilters {
  search: string
  gateway: ProcessorGateway | null
  certificateStatus: ProcessorCertificateStatus | null
  expiringOnly: boolean
}

export const EMPTY_PANEL_FILTERS: PanelFilters = {
  search: "",
  gateway: null,
  certificateStatus: null,
  expiringOnly: false,
}

/**
 * Acento lateral (`border-l-4`) do card conforme o status de certificado.
 *
 * Mora aqui, e não em cada card, porque a mesma escala de cor precisa valer
 * para todos: dois mapas separados viram duas telas pintando o mesmo estado de
 * certificado com cores diferentes no dia em que alguém ajustar um deles.
 */
export const ACCENT_BY_CERTIFICATE_STATUS: Record<
  string,
  string
> = {
  VALID: "border-l-emerald-500",
  EXPIRING: "border-l-amber-500",
  EXPIRED: "border-l-red-500",
  NOT_DEFINED: "border-l-zinc-300",
  NOT_APPLICABLE: "border-l-zinc-300",
}

// Severidade p/ escolher o status agregado do card do processor.
const STATUS_SEVERITY: Record<
  ProcessorCertificateStatus,
  number
> = {
  EXPIRED: 4,
  EXPIRING: 3,
  NOT_DEFINED: 2,
  VALID: 1,
  NOT_APPLICABLE: 0,
}

// Pior status entre as linhas de um card.
export const worstCertificateStatus = (
  items: ProcessorConfigItem[]
): ProcessorCertificateStatus =>
  items.reduce<ProcessorCertificateStatus>(
    (worst, item) =>
      STATUS_SEVERITY[
        item.certificate?.status ?? "NOT_APPLICABLE"
      ] > STATUS_SEVERITY[worst]
        ? (item.certificate?.status ?? "NOT_APPLICABLE")
        : worst,
    "NOT_APPLICABLE"
  )

// Menor daysToExpire entre as linhas do card (p/ "expira em Nd").
export const minDaysToExpire = (
  items: ProcessorConfigItem[]
): number | null =>
  items.reduce<number | null>((min, item) => {
    const days = item.certificate?.daysToExpire
    if (days === null || days === undefined) return min
    return min === null || days < min ? days : min
  }, null)

// Filtro client-side: busca por white label (nome/id), processor,
// status de certificado e "só cert expirando" (banner).
export const filterConfigs = (
  items: ProcessorConfigItem[],
  filters: PanelFilters
): ProcessorConfigItem[] => {
  const search = filters.search.trim().toLowerCase()
  return items.filter((item) => {
    if (
      search &&
      !(item.whiteLabelName ?? "")
        .toLowerCase()
        .includes(search) &&
      !item.whiteLabelId.toLowerCase().includes(search)
    ) {
      return false
    }
    if (filters.gateway && item.gateway !== filters.gateway)
      return false
    if (
      filters.certificateStatus &&
      (item.certificate?.status ?? "NOT_APPLICABLE") !==
        filters.certificateStatus
    ) {
      return false
    }
    if (
      filters.expiringOnly &&
      item.certificate?.status !== "EXPIRING"
    ) {
      return false
    }
    return true
  })
}

// Nº de configs com certificado expirando (<30d) — banner do topo.
export const countExpiringCertificates = (
  items: ProcessorConfigItem[]
): number =>
  items.filter(
    (item) => item.certificate?.status === "EXPIRING"
  ).length

/**
 * Se a config é o padrão do par — SEMPRE ler por aqui, nunca direto do campo:
 * backend anterior à Fase 1 do plano não envia `defaultForMethod`, e a ausência
 * significa "não é o padrão", nunca um estado quebrado.
 */
export const isDefaultForMethod = (
  item: ProcessorConfigItem
): boolean => item.defaultForMethod ?? false

// Seção por white label dentro do grupo do método (aba Nova Implementação).
/**
 * Folha das duas visões da tela: o par (white label, método).
 *
 * As duas abas são a MESMA árvore com os níveis trocados — por método → white
 * label, ou por white label → método — e nas duas a folha é este par. Não é
 * coincidência: o par é a unidade de todas as regras de negócio da tela (o
 * índice único da V176, o "no máximo um padrão", o badge "sem padrão", o aviso
 * de última ativa). Por isso o card de par serve as duas visões sem variante.
 */
export interface PairGroup {
  whiteLabelId: string
  whiteLabelName: string
  method: ProcessorPaymentMethod
  items: ProcessorConfigItem[]
  // Pior status de certificado entre as linhas — mesma regra do card antigo.
  certificateStatus: ProcessorCertificateStatus
  /**
   * Par com ≥1 config ativa e NENHUM padrão ATIVO — o estado que o backfill da
   * V176 deixa para a operação resolver; a UI o destaca com o badge "sem
   * padrão". Um default INATIVO não conta: ele não processa nada, então o par
   * continua efetivamente sem padrão.
   */
  hasActiveWithoutDefault: boolean
}

/** Dimensão do nível externo do accordion. */
export type PanelGrouping = "byMethod" | "byWhiteLabel"

/** Nível externo do accordion — método de pagamento ou white label. */
export interface ConfigGroup {
  /** Chave bruta: o método (visão por método) ou o id do white label. */
  key: string
  /**
   * Rótulo do accordion. Na visão por white label já vem pronto (o nome); na
   * visão por método vem o enum cru, porque traduzir depende do `t()` e este
   * módulo é puro — quem renderiza aplica `methods.<enum>` com fallback.
   */
  label: string
  /** Identificador copiável do grupo — o `WHL-` quando o grupo é o white label. */
  code: string | null
  pairs: PairGroup[]
  configCount: number
}

// Ordem fixa de negócio dos métodos; desconhecidos vão ao fim, alfabéticos.
const METHOD_DISPLAY_ORDER: Partial<
  Record<ProcessorPaymentMethod, number>
> = {
  CREDIT_CARD: 0,
  PIX: 1,
  BANK_SLIP: 2,
  BOLEPIX: 3,
}

const compareMethods = (
  a: ProcessorPaymentMethod,
  b: ProcessorPaymentMethod
): number => {
  const rankA = METHOD_DISPLAY_ORDER[a]
  const rankB = METHOD_DISPLAY_ORDER[b]
  if (rankA !== undefined && rankB !== undefined)
    return rankA - rankB
  if (rankA !== undefined) return -1
  if (rankB !== undefined) return 1
  return a.localeCompare(b)
}

/** Monta a folha (par WL+método) a partir das linhas visíveis do par. */
const buildPair = (
  pairItems: ProcessorConfigItem[],
  allItems: ProcessorConfigItem[]
): PairGroup => {
  const { whiteLabelId, method } = pairItems[0]
  // Flags do par sobre a lista NÃO filtrada — o que está escondido pelo
  // filtro continua contando para o padrão.
  const wholePair = allItems.filter(
    (item) =>
      item.method === method &&
      item.whiteLabelId === whiteLabelId
  )

  return {
    whiteLabelId,
    whiteLabelName:
      pairItems[0]?.whiteLabelName ?? whiteLabelId,
    method,
    items: [...pairItems].sort((a, b) =>
      a.gateway.localeCompare(b.gateway)
    ),
    certificateStatus: worstCertificateStatus(pairItems),
    hasActiveWithoutDefault:
      wholePair.some((item) => item.active) &&
      // Só um default ATIVO resolve o par — default inativo não
      // processa nada (V176).
      !wholePair.some(
        (item) => item.active && isDefaultForMethod(item)
      ),
  }
}

// Agrupa uma lista em buckets preservando a ordem de inserção.
const bucketBy = <T>(
  items: T[],
  keyOf: (item: T) => string
): Map<string, T[]> => {
  const buckets = new Map<string, T[]>()
  items.forEach((item) => {
    const key = keyOf(item)
    buckets.set(key, [...(buckets.get(key) ?? []), item])
  })
  return buckets
}

/**
 * Agrupa a lista em dois níveis para qualquer uma das duas visões da tela.
 *
 * A única diferença entre elas é QUAL dimensão fica no accordion externo — a
 * folha é sempre o par (white label, método). Por isso existe uma função só:
 * duas implementações paralelas divergiriam na primeira regra nova.
 *
 * Ordenação: a dimensão método usa a ordem fixa de negócio (cartão, pix,
 * boleto, bolepix; desconhecidos ao fim), a dimensão white label usa o nome.
 *
 * `allItems` é a lista NÃO filtrada de onde as flags do par derivam
 * (`hasActiveWithoutDefault`): um filtro que esconde o irmão default não pode
 * fabricar um "sem padrão" falso no que sobrou visível.
 */
export const groupConfigsBy = (
  items: ProcessorConfigItem[],
  grouping: PanelGrouping,
  allItems: ProcessorConfigItem[] = items
): ConfigGroup[] => {
  const byMethod = grouping === "byMethod"

  const outer = bucketBy(items, (item) =>
    byMethod ? item.method : item.whiteLabelId
  )

  return Array.from(outer.entries())
    .map(([key, groupItems]): ConfigGroup => {
      const inner = bucketBy(groupItems, (item) =>
        byMethod ? item.whiteLabelId : item.method
      )
      const pairs = Array.from(inner.values()).map(
        (pairItems) => buildPair(pairItems, allItems)
      )

      return {
        key,
        label: byMethod
          ? key
          : (groupItems[0]?.whiteLabelName ?? key),
        code: byMethod ? null : key,
        configCount: groupItems.length,
        pairs: pairs.sort((a, b) =>
          byMethod
            ? a.whiteLabelName.localeCompare(
                b.whiteLabelName
              )
            : compareMethods(a.method, b.method)
        ),
      }
    })
    .sort((a, b) =>
      byMethod
        ? compareMethods(
            a.key as ProcessorPaymentMethod,
            b.key as ProcessorPaymentMethod
          )
        : a.label.localeCompare(b.label)
    )
}

// True quando o item é a ÚLTIMA config ativa do par WL+método —
// desativar exige o warning extra no AlertDialog (§6.1).
export const isLastActiveOfPair = (
  items: ProcessorConfigItem[],
  target: ProcessorConfigItem
): boolean => {
  if (!target.active) return false
  return (
    items.filter(
      (item) =>
        item.active &&
        item.whiteLabelId === target.whiteLabelId &&
        item.method === target.method
    ).length === 1
  )
}
